import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getAiFirmSetting, updateAiFirmSetting } from 'api/ai-firm-settings';
import { AlertMessage } from 'utilities/utils';

/**
 * Hook to fetch the AI firm setting with long cache time
 */
export function useAiFirmSetting() {
  return useQuery({
    queryKey: ['ai-firm-setting'],
    queryFn: async () => {
      const result = await getAiFirmSetting();
      // Ensure we never return undefined - default to false if no data
      return result ?? false;
    },
    staleTime: 1000 * 60 * 60, // 1 hour - very long cache time
    cacheTime: 1000 * 60 * 60 * 2, // 2 hours - keep in cache longer
  });
}

/**
 * Hook to update the AI firm setting
 */
export function useUpdateAiFirmSetting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateAiFirmSetting,
    onSuccess: (data) => {
      // Only update cache if we have valid data
      if (data !== undefined && data !== null) {
        queryClient.setQueryData(['ai-firm-setting'], data);
      }
    },
    onError: (error: any) => {
      AlertMessage(
        'error',
        error.message || 'Failed to update AI setting',
        3000,
      );
    },
  });
}

/**
 * Combined hook that checks both the Launch Darkly feature flag and the firm setting
 * to determine if AI features should be enabled.
 *
 * Returns:
 * - isAiEnabled: boolean indicating if AI should be enabled
 * - isLoading: boolean indicating if the firm setting is still loading
 * - error: any error from fetching the firm setting
 * - firmSetting: the current firm setting value
 * - featureFlag: the current feature flag value
 */
export function useAiEnabled() {
  const { enableAi: featureFlag } = useFlags();
  const { data: firmSetting, isLoading, error } = useAiFirmSetting();

  // AI is enabled only if BOTH the feature flag is true AND the firm setting is true
  const isAiEnabled = Boolean(featureFlag && firmSetting);

  return {
    isAiEnabled,
    isLoading,
    error,
    firmSetting,
    featureFlag,
  };
}
